import { Transform, plainToInstance } from "class-transformer";
import { hasKey } from "../../util/object.util";
import { VariantDeliveryMethodEnum } from "./contract/form-template-contract";
import { QuestionTypeEnum } from "./form-template-question.model";
import { QuestionNPS } from "./question-NPS.model";
import { QuestionDate } from "./question-date.model";
import { QuestionList } from "./question-list.model";
import { QuestionNumeric } from "./question-numeric.model";
import { QuestionRange } from "./question-range.model";
import { QuestionRank } from "./question-rank.model";
import { QuestionText } from "./question-text.model";

export enum SectionDirection {
  Vertical = "vertical",
  Horizontal = "horizontal",
}

export enum FormTemplateSectionType {
  Question = "Question",
  Text = "Text",
  Introduction = "Introduction",
}

export class FormTemplateSection {
  id: number;
  parentFormTemplateSectionId: number | null;
  order: number;
  @Transform(({ value }) => decodeURIComponent(value || ""))
  value: string;
  description: string;
  // https://www.notion.so/FormTemplate-viewMeta-fb305e5f930a42f097e998cf88cbed6f
  viewMeta: {
    noPageBreaker?: boolean;
    width?: number;
    height?: number;
    rowBreak?: boolean;
  } | null;
  formTemplateSectionType: {
    name: FormTemplateSectionType;
    description: string;
  };
  @Transform(({ value: formTemplateQuestions }) => {
    return formTemplateQuestions
      .map((q) => {
        if (q.parentFormTemplateSectionId !== undefined && q.parentFormTemplateSectionId !== null) {
          return plainToInstance(FormTemplateSection, q);
        } else {
          switch (q.type) {
            case QuestionTypeEnum.Range:
              return plainToInstance(QuestionRange, q);
            case QuestionTypeEnum.List:
              return plainToInstance(QuestionList, q);
            case QuestionTypeEnum.Rank:
              return plainToInstance(QuestionRank, q);
            case QuestionTypeEnum.Date:
              return plainToInstance(QuestionDate, q);
            case QuestionTypeEnum.Numeric:
              return plainToInstance(QuestionNumeric, q);
            case QuestionTypeEnum.LongResponse:
            case QuestionTypeEnum.ShortResponse:
              return plainToInstance(QuestionText, q);
            case QuestionTypeEnum.NPS:
              return plainToInstance(QuestionNPS, q);
            default:
              return q;
          }
        }
      })
      .filter((q) => q !== undefined);
  })
  formTemplateQuestions: (
    | FormTemplateSection
    | QuestionRange
    | QuestionList
    | QuestionText
    | QuestionDate
    | QuestionNumeric
    | QuestionRank
    | QuestionNPS
  )[];

  public static isFormTemplateSection<T>(
    section:
      | FormTemplateSection
      | QuestionRange
      | QuestionList
      | QuestionText
      | QuestionDate
      | QuestionNumeric
      | QuestionRank
      | QuestionNPS
  ): section is FormTemplateSection {
    return hasKey(section, "parentFormTemplateSectionId");
  }

  questionCount(variantDeliveryMethod: VariantDeliveryMethodEnum): number {
    const count = (
      items: (
        | FormTemplateSection
        | QuestionRange
        | QuestionList
        | QuestionText
        | QuestionDate
        | QuestionNumeric
      )[]
    ): number =>
      items.reduce<number>((total, item) => {
        if (FormTemplateSection.isFormTemplateSection(item)) {
          return total + count(item.formTemplateQuestions);
        } else {
          if (
            variantDeliveryMethod === VariantDeliveryMethodEnum.online &&
            item?.questionVariant?.variantDeliveryMethod.isOnline
          ) {
            return total + 1;
          } else if (
            variantDeliveryMethod === VariantDeliveryMethodEnum.offline &&
            !item?.questionVariant?.variantDeliveryMethod.isOnline
          ) {
            return total + 1;
          }
          return total;
        }
      }, 0);
    return count(this.formTemplateQuestions);
  }

  get questions() {
    return this.formTemplateQuestions.reduce((questionsAcc, questionCurr) => {
      if (FormTemplateSection.isFormTemplateSection(questionCurr)) {
        const sectionQuestions = questionCurr.formTemplateQuestions.reduce(
          (subSectionQAcc, subSectionQCurr) =>
            FormTemplateSection.isFormTemplateSection(subSectionQCurr)
              ? subSectionQAcc
              : [...subSectionQAcc, subSectionQCurr],
          []
        );
        return [...questionsAcc, ...sectionQuestions];
      } else {
        return [...questionsAcc, questionCurr];
      }
    }, []);
  }
}
